import axios from "axios";
import { useContext } from "react";
import { getFile } from "../components/jsonForms/fileUpload/fileStore";
import { STORE_IMPORT_SAMPLE_CSV } from "../constants/storeImportSample";
import { GlobalConfigContext } from "../context/GlobalConfigContext";
import { uploadWithPolling } from "../utils/fileTransfer";

export function useKeywordFileUpload() {
  const { globalState } = useContext(GlobalConfigContext);
  const accessToken = JSON.parse(localStorage.getItem("okta-token-storage"))
    ?.accessToken?.accessToken;
  const programId =
    globalState?.programConfig?.id || "018a2bd5-fdc2-77ce-9367-7d700186d5ae";

  const handleError = (message, error) => {
    console.error(message, error);
    return { status: false, message };
  };

  const getAccessToken = () => accessToken || localStorage.getItem("accessToken");

  const extractFilesToUpload = (files) => {
    const filesToUpload = [];
    Array.from(files).forEach((fileItem) => {
      let actualFile = null;
      if (fileItem?.file instanceof File) {
        actualFile = fileItem.file;
      } else if (fileItem instanceof File) {
        actualFile = fileItem;
      } else if (fileItem?.name) {
        actualFile = getFile(fileItem.name);
      }

      if (fileItem?.id == null && actualFile instanceof File) {
        filesToUpload.push(actualFile);
      }
    });
    return filesToUpload;
  };

  const getImportErrorMessage = (error) => {
    const issues = error?.response?.data?.extension?.issues;
    if (Array.isArray(issues) && issues.length > 0) {
      let message = issues
        .map((issue) => issue.detail)
        .filter(Boolean)
        .join(". ");
      if (message && !message.endsWith(".")) {
        message += ".";
      }
      return message || "Something went wrong";
    }
    return issues?.detail || "Something went wrong";
  };

  const handleImportFileCall = async (files) => {
    const filesToUpload = extractFilesToUpload(files);

    if (filesToUpload.length === 0) {
      return {
        status: false,
        message: "No file selected to import. Please choose a file and try again.",
      };
    }

    try {
      const result = await uploadWithPolling({
        endpointBaseURI: window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI,
        context: "KEYWORD_GROUPS",
        file: filesToUpload[0],
        accessToken: getAccessToken(),
        programId,
        autoProcess: true,
        forceUpload: true,
        polling: false,
      });

      return { status: true, data: result?.response?.data };
    } catch (error) {
      return handleError(getImportErrorMessage(error), error);
    }
  };

  const handleDownloadSampleFile = async (
    fileType = "text/csv",
    downloadFileName = "keyword_sample.csv"
  ) => {
    try {
      const blob = new Blob([STORE_IMPORT_SAMPLE_CSV], { type: fileType });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return null;
    } catch (error) {
      return `There was an error downloading the file: ${error}`;
    }
  };

  const handleFileDownload = async (
    fileName,
    containerName = "keyword-groups-upload"
  ) => {
    try {
      const uploadUrl = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
      const params = new URLSearchParams({
        program_id: programId,
        container_name: containerName,
        file_name: fileName,
      });
      const endpoint = `${uploadUrl}download?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "program-id": programId,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { status: true, message: "File downloaded successfully" };
    } catch (error) {
      return handleError("An error occurred while downloading the file.", error);
    }
  };

  return {
    handleImportFileCall,
    handleDownloadSampleFile,
    handleFileDownload,
  };
}
